import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "@tanstack/react-query";
import { GetCompanySettings, UpdateCompanySettings } from "../../../lib/api";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { ShowLoadingAnimation } from "../../../components/common/Animation";
import {
  FaUser,
  FaMobileAlt,
  FaPrint,
  FaCogs,
  FaTicketAlt,
  FaBullhorn,
} from "react-icons/fa";
// import { MdSettingsVoice } from "react-icons/md";

const Settings = () => {
  const { isLoading, data, refetch } = useQuery({
    queryKey: ["GetCompanySettings"],
    queryFn: () => GetCompanySettings(),
  });

  const Company = data?.data?.companySettings;
  const [isLoadingData, setIsLoadingData] = useState(false);

  const { register, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      token: Company?.maxToken || "",
      customerName: Company?.CollectCustomerName || false,
      customerMobileNumber: Company?.CollectCustomerMobileNumber || false,
      printCustomerName: Company?.PrintCustomerName || false,
      printCustomerMobileNumber: Company?.PrintCustomerMobileNumber || false,
      service: Company?.IsServiceEnable || false,
    },
  });

  const isMobileNumberChecked = watch("customerMobileNumber");
  const isCustomerNameChecked = watch("customerName");
  const [isServiceEnabled, setIsServiceEnabled] = useState(
    Company?.IsServiceEnable || false
  );
  const [selectedToken, setSelectedToken] = useState(
    Company?.IsCustomCall ? "manualCalling" : "tokenBooking"
  );

  const handleTokenChange = (event) => {
    setSelectedToken(event.target.value);
  };

  useEffect(() => {
    if (data) {
      setValue("token", Company?.maxToken || "");
      setValue("customerName", Company?.CollectCustomerName || false);
      setValue(
        "customerMobileNumber",
        Company?.CollectCustomerMobileNumber || false
      );
      setValue("printCustomerName", Company?.PrintCustomerName || false);
      setValue(
        "printCustomerMobileNumber",
        Company?.PrintCustomerMobileNumber || false
      );
      setValue("service", Company?.IsServiceEnable || false);
      setIsServiceEnabled(Company?.IsServiceEnable || false);
      setSelectedToken(
        Company?.IsCustomCall ? "manualCalling" : "tokenBooking"
      );
    }
  }, [data, setValue, Company]);

  useEffect(() => {
    if (!isMobileNumberChecked) {
      setValue("printCustomerMobileNumber", false);
    }
    if (!isCustomerNameChecked) {
      setValue("printCustomerName", false);
    }
  }, [isMobileNumberChecked, isCustomerNameChecked, setValue]);

  useEffect(() => {
    Cookies.set("ServiceEnable", isServiceEnabled, { expires: 7 });
  }, [isServiceEnabled]);

  const mutate = useMutation({
    mutationFn: (updatedData) => UpdateCompanySettings(updatedData),
    onMutate: () => {
      setIsLoadingData(true);
    },
    onSuccess: () => {
      toast.success("Settings Updated", {
        position: "top-right",
        closeOnClick: true,
      });
      refetch();
    },
    onError: () => {
      toast.error("Failed to update settings", {
        position: "top-right",
        closeOnClick: true,
      });
    },
    onSettled: () => {
      setIsLoadingData(false);
    },
  });

  const onSubmit = async (data) => {
    const CompanyId = Cookies.get("CompanyId");
    const updatedData = {
      companyId: CompanyId,
      maximumToken: data.token,
      collectCustomerName: data.customerName,
      printCustomerName: data.printCustomerName,
      collectCustomerMobileNumber: data.customerMobileNumber,
      printCustomerMobileNumber: data.printCustomerMobileNumber,
      IsServiceEnable: isServiceEnabled,
      IsCustomCall: selectedToken === "manualCalling",
    };
    mutate.mutate(updatedData);
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-8 flex items-center">
        <FaCogs className="mr-3 text-blue-600" /> Company Settings
      </h1>
      {isLoading && (
        <div className="w-44 m-auto">
          <ShowLoadingAnimation />
        </div>
      )}
      {!isLoading && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FaUser className="mr-2 text-blue-600" /> Customer Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-3">
                  <input
                    {...register("customerMobileNumber")}
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 rounded"
                  />
                  <span className="text-gray-700 flex items-center">
                    <FaMobileAlt className="mr-2 text-blue-600" /> Collect
                    Mobile Number
                  </span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    {...register("printCustomerMobileNumber")}
                    disabled={!isMobileNumberChecked}
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 rounded"
                  />
                  <span className="text-gray-700 flex items-center">
                    <FaPrint className="mr-3 text-blue-600" /> Print Mobile
                    Number
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-3">
                  <input
                    {...register("customerName")}
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 rounded"
                  />
                  <span className="text-gray-700 flex items-center">
                    <FaUser className="mr-3 text-blue-600" /> Collect Customer
                    Name
                  </span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    {...register("printCustomerName")}
                    disabled={!isCustomerNameChecked}
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 rounded"
                  />
                  <span className="text-gray-700 flex items-center">
                    <FaPrint className="mr-2 text-blue-600" /> Print Customer
                    Name
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FaCogs className="mr-2 text-blue-600" /> Service Settings
            </h2>
            <div className="space-y-4">
              <label className="flex items-center space-x-3">
                <input
                  {...register("service")}
                  type="checkbox"
                  checked={isServiceEnabled}
                  onChange={(e) => setIsServiceEnabled(e.target.checked)}
                  className="form-checkbox h-5 w-5 text-blue-600 rounded"
                />
                <span className="text-gray-700 flex items-center">
                  <FaCogs className="mr-2 text-blue-600" /> Enable Service
                </span>
              </label>
            </div>
          </div>

          {/* <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <MdSettingsVoice className="mr-2 text-blue-600" /> Voice Settings
            </h2>
            <div className="space-y-4">
              <div className="flex flex-col">
                <label
                  htmlFor="language"
                  className="text-sm font-medium text-gray-700 mb-1"
                >
                  Select Voice for Token Calling
                </label>
                <select
                  id="language"
                  name="language"
                  className="w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="zh">Chinese</option>
                  <option value="ja">Japanese</option>
                </select>
              </div>
            </div>
          </div> */}

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FaTicketAlt className="mr-2 text-blue-600" /> Token System
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                className={`p-6 rounded-lg transition-all duration-300 ${
                  selectedToken === "tokenBooking"
                    ? "border-2 border-blue-500 bg-blue-50"
                    : "border-2 border-gray-200 hover:border-blue-300"
                }`}
              >
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="tokenOption"
                    value="tokenBooking"
                    checked={selectedToken === "tokenBooking"}
                    onChange={handleTokenChange}
                    className="form-radio h-5 w-5 text-blue-600"
                  />
                  <span className="text-xl font-semibold text-gray-800 flex items-center">
                    <FaTicketAlt className="mr-2 text-blue-600" /> Token Booking
                    System
                  </span>
                </label>
                <p className="mt-4 text-gray-600">
                  Customers can either walk in or use a booking system to get a
                  token. Tokens are called in order at the counter, ensuring an
                  organized process.
                </p>
              </div>
              <div
                className={`p-6 rounded-lg transition-all duration-300 ${
                  selectedToken === "manualCalling"
                    ? "border-2 border-green-500 bg-green-50"
                    : "border-2 border-gray-200 hover:border-green-300"
                }`}
              >
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="tokenOption"
                    value="manualCalling"
                    checked={selectedToken === "manualCalling"}
                    onChange={handleTokenChange}
                    className="form-radio h-5 w-5 text-green-600"
                  />
                  <span className="text-xl font-semibold text-gray-800 flex items-center">
                    <FaBullhorn className="mr-2 text-green-600" /> Manual Token
                    Calling
                  </span>
                </label>
                <p className="mt-4 text-gray-600">
                  Tokens are not generated in this mode. Staff can manually call
                  customers by their assigned number without issuing any tokens.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoadingData}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300"
            >
              {isLoadingData ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Settings"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Settings;
